/**
 * Row builders for Daily Transaction Sheet sections that need grouped subtotals.
 */

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

/**
 * Bag-collection short_excess is stored as 1/2 (or SHORT/EXCESS after normalize).
 * Never show the raw code or amount in the type column.
 */
export function formatShortExcessLabel(row) {
  const raw = row?.short_excess ?? row?.shortExcessLabel ?? row?.shortExcess;
  const text = String(raw ?? '').trim().toUpperCase();

  if (text === '1' || text === 'SHORT') {
    return 'Short';
  }
  if (text === '2' || text === 'EXCESS') {
    return 'Excess';
  }
  if (toNumber(row?.short_amount ?? row?.shortAmount) > 0) {
    return 'Short';
  }
  if (toNumber(row?.excess_amount ?? row?.excessAmount) > 0) {
    return 'Excess';
  }

  return '';
}

function buildGroupedRows(rows, { getGroupKey, createTotals, addToTotals, createSubtotal, mapDataRow }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { bodyRows: [] };
  }

  const bodyRows = [];
  let groupBuffer = null;
  let totals = createTotals();

  rows.forEach((row, index) => {
    const groupKey = getGroupKey(row) || '';

    if (groupBuffer !== null && groupBuffer !== groupKey) {
      bodyRows.push(createSubtotal(groupBuffer, totals));
      totals = createTotals();
    }

    groupBuffer = groupKey;
    addToTotals(totals, row);
    bodyRows.push(mapDataRow(row, index));
  });

  if (groupBuffer !== null) {
    bodyRows.push(createSubtotal(groupBuffer, totals));
  }

  return { bodyRows };
}

/** Group all rows by key (not only consecutive), preserving first-seen key order. */
function buildGroupedRowsByKey(rows, { getGroupKey, createTotals, addToTotals, createSubtotal, mapDataRow }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { bodyRows: [] };
  }

  const groupOrder = [];
  const groups = new Map();

  rows.forEach((row, index) => {
    const groupKey = getGroupKey(row) || '';
    if (!groups.has(groupKey)) {
      groups.set(groupKey, { entries: [], totals: createTotals() });
      groupOrder.push(groupKey);
    }

    const group = groups.get(groupKey);
    group.entries.push({ row, index });
    addToTotals(group.totals, row);
  });

  const bodyRows = [];
  groupOrder.forEach((groupKey) => {
    const group = groups.get(groupKey);
    group.entries.forEach(({ row, index }) => {
      bodyRows.push(mapDataRow(row, index));
    });
    bodyRows.push(createSubtotal(groupKey, group.totals));
  });

  return { bodyRows };
}

export function buildCylinderSalesTableRows(rows) {
  return buildGroupedRows(rows, {
    getGroupKey: (row) => row.product_name || '',
    createTotals: () => ({ amountSum: 0 }),
    addToTotals: (totals, row) => {
      totals.amountSum += toNumber(row.sales_value ?? row.dr_amount);
    },
    createSubtotal: (groupLabel, totals) => ({
      type: 'subtotal',
      groupLabel,
      amountSum: totals.amountSum,
    }),
    mapDataRow: (row, index) => ({
      type: 'data',
      key: `cyl-${index}`,
      productName: row.product_name,
      customerName: row.cust_name,
      qty: row.qty,
      salesPrice: row.sales_price,
      debitAmount: row.dr_amount,
      creditAmount: row.cr_amount,
    }),
  });
}

export function buildOilBarrelSalesTableRows(rows) {
  return buildGroupedRows(rows, {
    getGroupKey: (row) => row.product_full_name || '',
    createTotals: () => ({ amountSum: 0 }),
    addToTotals: (totals, row) => {
      const lineAmount = row.line_amount ?? row.total ?? row.dr_amount;
      totals.amountSum += toNumber(lineAmount);
    },
    createSubtotal: (groupLabel, totals) => ({
      type: 'subtotal',
      groupLabel,
      amountSum: totals.amountSum,
    }),
    mapDataRow: (row, index) => ({
      type: 'data',
      key: `los-${index}`,
      productName: row.product_full_name,
      customerName: row.name,
      fullBarrelQty: row.fullBarrelQty,
      looseBarrelQty: row.looseBarrelSalesQty,
      fullBarrelMrp: row.fullBarrelMrp,
      looseBarrelMrp: row.looseBarrelMrp,
      machineSales: row.machine_sales,
      debitAmount: row.dr_amount,
      creditAmount: row.cr_amount,
    }),
  });
}

export function buildCreditSalesTableRows(rows) {
  return buildGroupedRows(rows, {
    getGroupKey: (row) => row.name || 'New Customer',
    createTotals: () => ({ amountSum: 0 }),
    addToTotals: (totals, row) => {
      totals.amountSum += toNumber(row.amount);
    },
    createSubtotal: (groupLabel, totals) => ({
      type: 'subtotal',
      groupLabel,
      amountSum: totals.amountSum,
    }),
    mapDataRow: (row, index) => ({
      type: 'data',
      key: `credit-${index}`,
      customerName: row.name || 'New Customer',
      intentNo: row.intent_no,
      vehicleNo: row.vehicle_no || 'New Vehicle no',
      fuelName: row.fuel_name,
      quantity: row.quantity,
      price: row.price,
      note: row.note,
      amount: row.amount,
    }),
  });
}

export function buildWorksheetRecoupTableRows(rows) {
  return buildGroupedRowsByKey(rows, {
    getGroupKey: (row) => row.fuel_name || '',
    createTotals: () => ({ amountSum: 0, quantitySum: 0 }),
    addToTotals: (totals, row) => {
      totals.amountSum += toNumber(row.amount);
      totals.quantitySum += toNumber(row.quantity);
    },
    createSubtotal: (groupLabel, totals) => ({
      type: 'subtotal',
      groupLabel,
      amountSum: totals.amountSum,
      quantitySum: totals.quantitySum,
    }),
    mapDataRow: (row, index) => ({
      type: 'data',
      key: `worksheet-recoup-${index}`,
      opDate: row.op_date,
      fuelName: row.fuel_name,
      tankName: row.tank_name,
      quantity: row.quantity,
      price: row.price,
      note: row.note,
      amount: row.amount,
    }),
  });
}

export function buildWorksheetMlaCouponTableRows(rows) {
  return buildGroupedRows(rows, {
    getGroupKey: (row) => row.fuel_name || '',
    createTotals: () => ({ amountSum: 0, quantitySum: 0 }),
    addToTotals: (totals, row) => {
      totals.amountSum += toNumber(row.amount);
      totals.quantitySum += toNumber(row.quantity);
    },
    createSubtotal: (groupLabel, totals) => ({
      type: 'subtotal',
      groupLabel,
      amountSum: totals.amountSum,
      quantitySum: totals.quantitySum,
    }),
    mapDataRow: (row, index) => ({
      type: 'data',
      key: `worksheet-mla-${index}`,
      opDate: row.op_date,
      couponNo: row.coupon_no,
      issueDate: row.issue_date,
      expiryDate: row.expiry_date,
      note: row.note,
      fuelName: row.fuel_name,
      quantity: row.quantity,
      price: row.price,
      amount: row.amount,
    }),
  });
}

export function buildRecoupTableRows(rows) {
  return buildGroupedRows(rows, {
    getGroupKey: (row) => row.fuel_name || '',
    createTotals: () => ({ amountSum: 0, quantitySum: 0 }),
    addToTotals: (totals, row) => {
      totals.amountSum += toNumber(row.amount);
      totals.quantitySum += toNumber(row.quantity);
    },
    createSubtotal: (groupLabel, totals) => ({
      type: 'subtotal',
      groupLabel,
      amountSum: totals.amountSum,
      quantitySum: totals.quantitySum,
    }),
    mapDataRow: (row, index) => ({
      type: 'data',
      key: `recoup-${index}`,
      fuelName: row.fuel_name,
      tankName: row.tank_name,
      quantity: row.quantity,
      price: row.price,
      note: row.note,
      debitAmount: row.dr_amount,
      creditAmount: row.amount,
    }),
  });
}

export function buildDigitalPaymentTableRows(rows) {
  return buildGroupedRowsByKey(rows, {
    getGroupKey: (row) => row.method_name || row.accounts_head || '',
    createTotals: () => ({ amountSum: 0 }),
    addToTotals: (totals, row) => {
      totals.amountSum += toNumber(row.amount ?? row.cr_amount ?? row.creditAmount);
    },
    createSubtotal: (groupLabel, totals) => ({
      type: 'subtotal',
      groupLabel,
      amountSum: totals.amountSum,
    }),
    mapDataRow: (row, index) => ({
      type: 'data',
      key: `digital-${index}`,
      methodName: row.method_name || row.accounts_head || '',
      bankName: row.bank_name || '',
      cardNo: row.card_no || '',
      note: row.note || '',
      debitAmount: row.dr_amount,
      creditAmount: row.amount ?? row.cr_amount,
    }),
  });
}

export function buildExpenseVoucherTableRows(rows) {
  return buildGroupedRows(rows, {
    getGroupKey: (row) => row.ac_name_slug || row.name || '',
    createTotals: () => ({ amountSum: 0 }),
    addToTotals: (totals, row) => {
      totals.amountSum += toNumber(row.amount);
    },
    createSubtotal: (groupLabel, totals) => ({
      type: 'subtotal',
      groupLabel,
      amountSum: totals.amountSum,
    }),
    mapDataRow: (row, index) => ({
      type: 'data',
      key: `expense-${index}`,
      partyName: row.name || row.party_name || '',
      accountName: row.ac_name_slug || row.name || '',
      sysVoucherNo: row.sys_voucher_no,
      voucherNo: row.voucher_no,
      category: row.category,
      subCategory: row.sub_cat || '',
      note: row.note,
      amount: row.amount,
    }),
  });
}

export function buildShortExcessTableRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { bodyRows: [] };
  }

  const bodyRows = [];
  let employeeBuffer = null;
  let shortSum = 0;
  let excessSum = 0;

  const pushSubtotal = (employeeName) => {
    bodyRows.push({
      type: 'subtotal',
      groupLabel: employeeName,
      netAmount: excessSum - shortSum,
    });
  };

  rows.forEach((row, index) => {
    const employeeName = row.name || '';

    if (employeeBuffer !== null && employeeBuffer !== employeeName) {
      pushSubtotal(employeeBuffer);
      shortSum = 0;
      excessSum = 0;
    }

    employeeBuffer = employeeName;
    shortSum += toNumber(row.short_amount);
    excessSum += toNumber(row.excess_amount);

    bodyRows.push({
      type: 'data',
      key: `short-excess-${index}`,
      employeeName,
      shortAmount: row.short_amount,
      excessAmount: row.excess_amount,
      shortExcessLabel: formatShortExcessLabel(row),
      debitAmount: row.dr_amount,
      creditAmount: row.cr_amount,
    });
  });

  if (employeeBuffer !== null) {
    pushSubtotal(employeeBuffer);
  }

  return { bodyRows };
}

export function computeShortExcessGrandTotal(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return 0;
  }

  let grandTotal = 0;
  let employeeBuffer = null;
  let shortSum = 0;
  let excessSum = 0;

  rows.forEach((row) => {
    const employeeName = row.name || '';

    if (employeeBuffer !== null && employeeBuffer !== employeeName) {
      grandTotal += excessSum - shortSum;
      shortSum = 0;
      excessSum = 0;
    }

    employeeBuffer = employeeName;
    shortSum += toNumber(row.short_amount);
    excessSum += toNumber(row.excess_amount);
  });

  if (employeeBuffer !== null) {
    grandTotal += excessSum - shortSum;
  }

  return grandTotal;
}
